import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, FileSignature, Info } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const GESTIONALO_CERTIFICATES_URL =
  import.meta.env.VITE_GESTIONALO_CERTIFICATES_URL?.trim() ?? "";

export interface NotarialCertificateSectionProps {
  /**
   * When set, a confirmation checkbox is shown and this is called with true
   * when the user confirms. Omit for informational-only blocks (e.g. the
   * certificate-step subflow that gates Siguiente on a separate flag).
   */
  onCanAdvanceChange?: (canAdvance: boolean) => void;
  /** Distinct ids when multiple sections render on one page. */
  idPrefix?: string;
}

/**
 * Sub-section that surfaces inside the manual cert-acquisition step for
 * tenants whose company type (SAS / SRL / SA) requires Abitab to receive
 * a notarial certificate before ID Digital will issue the CFE-enabled
 * digital certificate. Gestionalo is Fiscalo's recommended provider for
 * the notarial trámite but is NOT mandatory — the user can hand-deliver
 * a notarial cert obtained any other way.
 *
 * Visual: editorial (paper-2 background, mono eyebrow, serif title,
 * sage accent for the recommended action) to match the surrounding
 * manual-cert step which was redesigned in the same pass.
 */
/**
 * ID Digital's own model for the notarial document. An escribano drafting from
 * any other template produces something Abitab refuses at the counter, so this
 * is the one link that has to reach whoever writes it.
 */
const NOTARIAL_TEMPLATE_URL =
  "https://cms.iddigital.com.uy/modelocertificadonotarial_V2025_02_13_3efa0cb532.pdf";

/**
 * Where the digitised documentation goes BEFORE the branch visit. Abitab's
 * wording is "es requisito adjuntar tu documentación digitalizada para evitar
 * demoras" — a requirement, not a shortcut.
 */
const NOTARIAL_UPLOAD_URL = "https://abitab.id.com.uy/pjnotarial";

export function NotarialCertificateSection({
  onCanAdvanceChange,
  idPrefix = "gestionalo",
}: NotarialCertificateSectionProps): JSX.Element {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  const requiresAcknowledgment = onCanAdvanceChange != null;

  useEffect(() => {
    if (!requiresAcknowledgment) return;
    onCanAdvanceChange(acknowledged);
  }, [acknowledged, onCanAdvanceChange, requiresAcknowledgment]);

  const checkboxId = `${idPrefix}-gestionalo-ack`;

  return (
    <section
      style={{
        border: "1px solid var(--fx-ink-200)",
        borderRadius: "var(--fx-r-md, 12px)",
        background: "var(--fx-s-raised-hover)",
        padding: "18px 20px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "var(--fx-ff-mono)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          color: "var(--fx-ink-500, #6B7280)",
          marginBottom: 12,
        }}
      >
        <FileSignature aria-hidden style={{ width: 14, height: 14 }} />
        {t(
          "onboarding.gestionaloNotarial.eyebrow",
          "Certificado notarial",
        )}
      </div>

      <h3
        style={{
          margin: 0,
          fontFamily: "var(--fx-ff-serif)",
          fontWeight: 500,
          fontSize: 20,
          lineHeight: 1.25,
          color: "var(--fx-s-text)",
        }}
      >
        {t(
          "onboarding.gestionaloNotarial.titleNew",
          "Tu tipo de empresa necesita un certificado notarial.",
        )}
      </h3>

      <p
        style={{
          marginTop: 8,
          marginBottom: 0,
          fontSize: 14,
          lineHeight: 1.6,
          color: "var(--fx-ink-800, #374151)",
        }}
      >
        {t(
          "onboarding.gestionaloNotarial.bodyWhy",
          "Por ser SAS, SRL o SA, Abitab te va a pedir un certificado notarial antes de habilitar el certificado digital para CFE.",
        )}
      </p>

      <div
        style={{
          marginTop: 14,
          padding: "12px 14px",
          border: "1px dashed var(--fx-accent-300)",
          borderRadius: "var(--fx-r-md, 12px)",
          background:
            "color-mix(in srgb, var(--fx-accent-300) 8%, var(--fx-paper))",
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
        }}
      >
        <Info
          aria-hidden
          style={{
            width: 16,
            height: 16,
            color: "var(--fx-accent-500)",
            marginTop: 2,
            flexShrink: 0,
          }}
        />
        <div>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.55,
              color: "var(--fx-s-text)",
            }}
          >
            <strong style={{ fontWeight: 600 }}>
              {t(
                "onboarding.gestionaloNotarial.recommendationLead",
                "Recomendamos Gestionalo",
              )}
            </strong>{" "}
            {t(
              "onboarding.gestionaloNotarial.recommendationBody",
              "para tramitar el certificado notarial — es rápido y 100% online. No es obligatorio: si preferís otro escribano o ya tenés uno, también funciona.",
            )}
          </p>
        </div>
      </div>

      {GESTIONALO_CERTIFICATES_URL ? (
        <div style={{ marginTop: 14 }}>
          <a
            href={GESTIONALO_CERTIFICATES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="fx-btn fx-btn-accent"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              textDecoration: "none",
            }}
          >
            {t("onboarding.gestionaloNotarial.openLink", "Abrir Gestionalo")}
            <ExternalLink
              aria-hidden
              style={{ width: 14, height: 14 }}
            />
          </a>
        </div>
      ) : null}

      {/* Abitab's own requirements, taken verbatim from the ID Digital
          confirmation email. Without these three the user can pay an escribano,
          wait, and still be turned away at the counter: wrong template, no
          digital copy uploaded, or a document more than 30 days old. */}
      <div
        style={{
          marginTop: 16,
          padding: "12px 14px",
          borderRadius: "var(--fx-r-md)",
          background: "rgba(45,97,73,0.05)",
          border: "1px solid rgba(45,97,73,0.16)",
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 12,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--fx-ink-500)",
          }}
        >
          {t("onboarding.gestionaloNotarial.abitabAsksTitle", "Abitab te va a pedir")}
        </p>
        <ul
          style={{
            margin: "10px 0 0",
            paddingLeft: 18,
            // Tailwind's preflight sets `ul { list-style: none }`, so without
            // this the four requirements render as one unbroken block of text
            // — which is how they shipped, and why they were unreadable.
            listStyle: "disc",
            display: "grid",
            gap: 8,
            fontSize: 13,
            lineHeight: 1.6,
            color: "var(--fx-s-text)",
          }}
        >
          <li>
            <strong style={{ fontWeight: 600 }}>
              {t(
                "onboarding.gestionaloNotarial.abitabAsksTemplateLead",
                "El modelo de ID Digital.",
              )}
            </strong>{" "}
            {t(
              "onboarding.gestionaloNotarial.abitabAsksTemplate",
              "Cualquier otro formato lo rechazan, así que pasale el modelo al escribano antes de que lo redacte.",
            )}{" "}
            <a
              href={NOTARIAL_TEMPLATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--fx-accent-500)", whiteSpace: "nowrap" }}
            >
              {t("onboarding.gestionaloNotarial.abitabAsksTemplateLink", "Ver el modelo (PDF)")}
            </a>
          </li>
          <li>
            <strong style={{ fontWeight: 600 }}>
              {t(
                "onboarding.gestionaloNotarial.abitabAsksUploadLead",
                "El escaneado subido antes de ir.",
              )}
            </strong>{" "}
            {t(
              "onboarding.gestionaloNotarial.abitabAsksUpload",
              "Abitab lo pide como requisito, no como sugerencia: sin eso te hacen volver.",
            )}{" "}
            <a
              href={NOTARIAL_UPLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--fx-accent-500)", whiteSpace: "nowrap" }}
            >
              {t("onboarding.gestionaloNotarial.abitabAsksUploadLink", "Adjuntar documentación")}
            </a>
          </li>
          <li>
            <strong style={{ fontWeight: 600 }}>
              {t(
                "onboarding.gestionaloNotarial.abitabAsksFresh",
                "Que tenga menos de 30 días.",
              )}
            </strong>{" "}
            {t(
              "onboarding.gestionaloNotarial.abitabAsksFreshBody",
              "Se cuentan desde que el escribano lo firma hasta que lo entregás, así que conviene pedirlo cerca de la visita y no antes.",
            )}
          </li>
          <li>
            <strong style={{ fontWeight: 600 }}>
              {t(
                "onboarding.gestionaloNotarial.abitabAsksOriginalLead",
                "El original en papel.",
              )}
            </strong>{" "}
            {t(
              "onboarding.gestionaloNotarial.abitabAsksOriginal",
              "Lo entregás en el local, además de la copia que subiste.",
            )}
          </li>
        </ul>
      </div>

      {requiresAcknowledgment && (
        <label
          htmlFor={checkboxId}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginTop: 14,
            padding: 12,
            border: "1px solid var(--fx-ink-200)",
            borderRadius: "var(--fx-r-md, 12px)",
            background: "var(--fx-s-overlay)",
            cursor: "pointer",
          }}
        >
          <Checkbox
            id={checkboxId}
            checked={acknowledged}
            onCheckedChange={(checked) => setAcknowledged(checked === true)}
            style={{ marginTop: 3 }}
          />
          <span
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: "var(--fx-s-text)",
            }}
          >
            {t(
              "onboarding.gestionaloNotarial.acknowledgeNew",
              "Entiendo que necesito un certificado notarial y voy a conseguirlo (con Gestionalo u otro escribano) antes de finalizar el trámite en Abitab.",
            )}
          </span>
        </label>
      )}
    </section>
  );
}
